import { ConnectProto } from "@racoon/api";

type Organization = ConnectProto.Organization;

/**
 * Whether `org` holds the very BIN looked up and is not the organization being edited.
 * Search matches loosely across an organization's fields, so a returned row alone is no clash.
 */
export function holdsBin(
  org: Organization | undefined,
  bin: string,
  excludeOrgId?: Organization["id"],
): org is Organization {
  // String(): an id arrives as a bigint from proto-es but as a decimal string off the wire.
  return org?.bin?.trim() === bin && String(org.id) !== String(excludeOrgId);
}

/**
 * The provider's counterparty that is a customer holding `bin`. Counterparties come from
 * contracts in both directions, whatever their type, and the same company may be a provider
 * and a customer at once — only another customer clashes with a customer.
 */
export function findCustomerBinDuplicate(
  counterparties: readonly ConnectProto.Counterparty[],
  bin: string,
  excludeOrgId?: Organization["id"],
): ConnectProto.Counterparty | undefined {
  return counterparties.find(
    (c) =>
      holdsBin(c.organization, bin, excludeOrgId) &&
      c.organization.type === ConnectProto.OrganizationType.SERVICE_RECEIVER,
  );
}

/** The customers list files a counterparty whose contracts are all INACTIVE under the archive. */
export const isArchivedCounterparty = (counterparty: ConnectProto.Counterparty): boolean =>
  counterparty.contractStatus === ConnectProto.ContractStatus.INACTIVE;

/**
 * The top-level organization holding `bin`, among organizations of one type. A branch shares
 * its parent's BIN by design, so only a top-level organization clashes.
 */
export function findOrganizationBinDuplicate(
  orgs: readonly Organization[],
  bin: string,
  excludeOrgId?: Organization["id"],
): Organization | undefined {
  return orgs.find((org) => holdsBin(org, bin, excludeOrgId) && !org.parentId);
}
